import { Avatar, IconButton, Media } from "@ameelio/ui";
import Info from "@mui/icons-material/Info";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  ConnectionFeature,
  ConnectionRestriction,
  Correspondent,
  PrivacyLevel,
} from "../api/graphql";
import { DateInterval } from "../lib/dateUtils";
import VoiceCallsButton from "../VoiceCall/VoiceCallsButton";

type Props = {
  showVoiceCall: boolean;
  mayMakeVoiceCall: boolean;
  nextVoiceCallSlot: DateInterval | null;
  connection: {
    id: string;
    features: ConnectionFeature[];
    activeRestrictions: Pick<ConnectionRestriction, "id" | "service">[];
  };
  contact: Pick<
    Correspondent,
    | "firstName"
    | "lastName"
    | "fullName"
    | "unavailableUntil"
    | "canReceiveVoiceCalls"
  >;
  onSeeDetails: () => void;
  detailsOpen: boolean;
  currentVoiceCallSlotPrivacyLevels: PrivacyLevel[] | null;
};

export default function ConnectionHeader({
  showVoiceCall,
  mayMakeVoiceCall,
  nextVoiceCallSlot,
  connection,
  contact,
  onSeeDetails,
  detailsOpen,
  currentVoiceCallSlotPrivacyLevels,
}: Props) {
  const { t } = useTranslation();
  const hasVoiceCallRestriction = Boolean(
    connection.activeRestrictions.find((r) => r.service === "VOICE_CALL")
  );
  return (
    <Box
      sx={{
        py: 1.5,
        px: 2,
        borderBottom: 1,
        borderColor: "divider",
        backgroundColor: "background.default",

        // 1100
        zIndex: "appBar",

        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Media image={<Avatar contact={contact} />}>
        <Typography variant="body1">{contact.fullName}</Typography>
      </Media>
      <Box>
        {showVoiceCall && (
          <VoiceCallsButton
            connectionId={connection.id}
            contact={contact}
            mayMakeVoiceCall={mayMakeVoiceCall}
            connectionAllowsVoiceCalls={connection.features.includes(
              ConnectionFeature.VoiceCall
            )}
            hasRestriction={hasVoiceCallRestriction}
            nextVoiceCallSlot={nextVoiceCallSlot}
            currentVoiceCallSlotPrivacyLevels={
              currentVoiceCallSlotPrivacyLevels
            }
          />
        )}
        <IconButton ariaLabel={t("Info")} size="large" onClick={onSeeDetails}>
          {detailsOpen ? (
            <Info color="primary" />
          ) : (
            <InfoOutlined color="primary" />
          )}
        </IconButton>
      </Box>
    </Box>
  );
}
